import {
    Button,
    DialogTitle,
    Dialog,
    DialogContent,
    DialogActions,
    DialogContentText,
    Typography,
    Stack,
    Box,
    CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { useTheme } from '@emotion/react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import './RoleSelectionDialog.css';
import RoleCard from './RoleCard';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { HANDLE_ROLE_SELECTION, LOGIN, SET_CREDS, STORE_MENU } from 'store/actions';
import { auth } from 'apis';
import { login, SwitchFunction } from 'apis/controllers/auth';
import { decodeToken } from 'utils';
import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';

const RoleSelectionDialog = (props) => {
    const { n1 = null, ...rest } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [disabledButtons, setDisabledButtons] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    const { openRoleSelection, roles, creds, token } = useSelector((state) => state.role);
    const { redirectTo } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const onClose = () => {
        dispatch({ type: HANDLE_ROLE_SELECTION, open: false });
        setDisabledButtons(false);
    };

    const handleSelectRole = async (item) => {
        if (!disabledButtons) {
            setDisabledButtons(true);
            if (creds) {
                const res = await auth.login({
                    username: creds?.email,
                    password: creds?.password,
                    selectedUrpfId: item?.value
                });
                const decodedToken = decodeToken(token);

                dispatch({ type: STORE_MENU, menuItems: res?.menu });
                dispatch({ type: LOGIN, payload: { ...res, original_name: decodedToken?.original_name } });
                dispatch({ type: SET_CREDS, creds: null });
                enqueueSnackbar(t('LoginSuccess'), {
                    variant: 'success'
                });
                const queryString = window.location.search;
                const urlParams = new URLSearchParams(queryString);
                const navUrl = urlParams.get('navUrl');
                if (navUrl) navigate(navUrl);
                else {
                    console.log(window.location, 'Mello');
                    navigate('/home');
                }
                onClose();
            } else {
                const res = await SwitchFunction({ urpfId: item?.value });
                const decodedToken = decodeToken(token);
                if (res?.menu) await dispatch({ type: STORE_MENU, menuItems: res?.menu });
                await dispatch({ type: LOGIN, payload: { ...res, original_name: decodedToken?.original_name } });
                await dispatch({ type: SET_CREDS, creds: null });
                navigate('/home');
                enqueueSnackbar(t('RoleSwitchSuccessful'), {
                    variant: 'success'
                });
                await queryClient.invalidateQueries('me');
                if (window?.location?.pathname === '/home' && redirectTo === res?.redirectTo) navigate(0);
                onClose();
            }
        }
    };

    return (
        <Dialog open={openRoleSelection ?? false} onClose={onClose} {...rest} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Typography variant="h3" sx={{ flexGrow: 1 }}>
                    {t('SelectRole')}
                </Typography>
            </DialogTitle>
            {disabledButtons ? (
                <DialogContent sx={{ display: 'flex', minHeight: 300, justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </DialogContent>
            ) : (
                <DialogContent sx={{ bgcolor: '#fefefe' }}>
                    <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={4} sx={{ mb: 0 }}>
                        <DialogContentText>
                            <Box
                                sx={{
                                    p: 1,
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'break-word'
                                }}
                            >
                                {t('SelectRolesDescription')}
                            </Box>
                        </DialogContentText>
                    </Stack>
                    <div className="role-card-grid">
                        {roles?.map((item, index) => (
                            <RoleCard key={index} data={item} isSelected={item?.selected} handleSelectRole={() => handleSelectRole(item)} />
                        ))}
                    </div>
                </DialogContent>
            )}
        </Dialog>
    );
};

export default RoleSelectionDialog;
